import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

type LandingTheme = 'light' | 'dark'

interface LandingNavProps {
  theme: LandingTheme
  onToggleTheme: () => void
  onSignIn: () => void
  onAbout?: (() => void) | undefined
  onPricing?: (() => void) | undefined
  isAuthenticated: boolean
}

function MoonIcon(): ReactElement {
  return (
    <svg
      className="lp-theme-toggle-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="var(--text-dim)"
      strokeWidth="1.5"
    >
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  )
}

function SunIcon(): ReactElement {
  return (
    <svg
      className="lp-theme-toggle-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="var(--text-dim)"
      strokeWidth="1.5"
    >
      <circle cx="12" cy="12" r="5" />
      <line x1="12" y1="1" x2="12" y2="3" />
      <line x1="12" y1="21" x2="12" y2="23" />
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
      <line x1="1" y1="12" x2="3" y2="12" />
      <line x1="21" y1="12" x2="23" y2="12" />
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
    </svg>
  )
}

function NavLinks({
  onAbout,
  onPricing,
  onSignIn,
  isAuthenticated,
  theme,
  onToggleTheme,
}: LandingNavProps): ReactElement {
  const { t } = useTranslation('common')
  const toggleLabel =
    theme === 'light' ? t('landing.themeToggle.dark') : t('landing.themeToggle.light')

  return (
    <div className="lp-nav-links">
      <button type="button" className="lp-nav-link" onClick={onAbout}>
        {t('landing.about')}
      </button>
      <button type="button" className="lp-nav-link" onClick={onPricing}>
        {t('landing.pricing')}
      </button>
      <button type="button" className="lp-nav-link lp-nav-link--cta" onClick={onSignIn}>
        {isAuthenticated ? t('landing.dashboard') : t('landing.signIn')}
      </button>
      <button
        type="button"
        className="lp-theme-toggle"
        onClick={onToggleTheme}
        aria-label={toggleLabel}
      >
        {theme === 'light' ? <MoonIcon /> : <SunIcon />}
        <span className="lp-theme-toggle-label">{toggleLabel}</span>
      </button>
    </div>
  )
}

export function LandingNav(props: LandingNavProps): ReactElement {
  return (
    <nav className="lp-nav">
      <div className="lp-nav-brand">
        <img className="lp-nav-logo" src="/evidenx-key-logo.png" alt="EvidenX" />
        <span className="lp-nav-wordmark">EvidenX</span>
      </div>
      <NavLinks {...props} />
    </nav>
  )
}
